import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { NumberInput } from '@euler/components/NumberInput';
import { Input } from '@euler/components/form/Input';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { dateFromValue } from '@euler/utils/datetime';
import {
  BottomSheetFooter,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import DateTimePicker, {
  DateTimePickerEvent,
} from '@react-native-community/datetimepicker';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Keyboard, Platform, ScrollView, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaintenancePlanListItem, MaintenancePlanListItemType } from '../types';

export const MaintenancePlanBottomSheet = memo(
  ({
    show,
    target,
    footerBottomInset,
    onClose,
    onDone,
    ...props
  }: {
    show?: boolean;
    target?: MaintenancePlanListItem;
    defaultNextServiceDate?: string | Date;
    defaultNextServiceMileage?: number;
    footerBottomInset?: number;
    onClose?: () => void;
    onDone?: (values: {
      id: string;
      name: string;
      type: MaintenancePlanListItemType;
      nextServiceDate: string | Date;
      nextServiceMileage: number | undefined;
      remark: string | undefined;
    }) => void;
  }) => {
    const insets = useSafeAreaInsets();
    const alert = useAlert();
    const { rpx } = useResponsiveContext();
    const ref = useRef<MaintenancePlanBottomSheetContentRef>(null);

    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show,
        minSnapPoint: '60%',
        mediumSnapPoint: '75%',
        dismissWhenPressBackdrop: true,
        enablePanDownToClose: true,
        onDismiss: onClose,
      });

    const onDonePress = useCallback(() => {
      const values = ref.current?.getValues();
      if (values == null) return;
      try {
        onDone?.(values);
        bottomSheetModalRef.current?.dismiss();
      } catch (e) {
        void alert({ title: (e as Error).message });
      }
    }, [alert, bottomSheetModalRef, onDone]);

    const ScrollViewComponent =
      Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

    const [isKeyboardVisible, setKeyboardVisible] = useState(false);
    useEffect(() => {
      const showListener = Keyboard.addListener('keyboardWillShow', () =>
        setKeyboardVisible(true),
      );
      const hideListener = Keyboard.addListener('keyboardWillHide', () =>
        setKeyboardVisible(false),
      );
      return () => {
        showListener.remove();
        hideListener.remove();
      };
    }, []);

    return (
      <BottomSheetModal
        ref={bottomSheetModalRef}
        keyboardBehavior="extend"
        // eslint-disable-next-line @typescript-eslint/no-shadow
        footerComponent={(props: any) => (
          <BottomSheetFooter
            {...props}
            style={
              footerBottomInset
                ? {
                    top: -footerBottomInset,
                  }
                : undefined
            }
          >
            <Center.Touchable
              onPress={onDonePress}
              css={`
                background-color: ${StatusColors.Info};
                height: ${rpx(44) + (isKeyboardVisible ? 0 : insets.bottom)}px;
                padding-bottom: ${isKeyboardVisible ? 0 : insets.bottom}px;
                margin-bottom: ${isKeyboardVisible ? rpx(30) : 0}px;
                margin-left: ${isKeyboardVisible ? rpx(20) : 0}px;
                margin-right: ${isKeyboardVisible ? rpx(20) : 0}px;
                border-radius: ${isKeyboardVisible ? rpx(5) : 0}px;
              `}
            >
              <Typography.Text
                bold
                size={rpx(18)}
                color="#fff"
                nativeID="confirm-plan"
              >
                {target?.type === 'insurance-expiry'
                  ? '确认保险到期信息'
                  : '确认下次保养信息'}
              </Typography.Text>
            </Center.Touchable>
          </BottomSheetFooter>
        )}
        {...bottomSheetProps}
      >
        {target ? (
          <ScrollViewComponent
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{
              paddingBottom: footerBottomInset ?? 0,
            }}
          >
            <MaintenancePlanBottomSheetContent
              ref={ref}
              target={target}
              {...props}
            />
          </ScrollViewComponent>
        ) : null}
      </BottomSheetModal>
    );
  },
);
type MaintenancePlanBottomSheetContentRef = {
  getValues: () =>
    | {
        id: string;
        name: string;
        type: MaintenancePlanListItemType;
        remark: string | undefined;
        nextServiceDate: string | Date;
        nextServiceMileage: number | undefined;
      }
    | undefined;
};
const MaintenancePlanBottomSheetContent = memo(
  forwardRef<
    MaintenancePlanBottomSheetContentRef,
    {
      target: MaintenancePlanListItem;
      defaultNextServiceDate?: string | Date;
      defaultNextServiceMileage?: number;
    }
  >(({ target, defaultNextServiceDate, defaultNextServiceMileage }, ref) => {
    const { rpx } = useResponsiveContext();
    const { isVoiceInputEnabled } = useVoiceFeatures();

    useToggleAutomaticKeyboard(true);

    const alert = useAlert();

    const [customPlanName, setCustomPlanName] = useState(
      target.type === 'custom' ? target.name : '',
    );
    const [remark, setRemark] = useState(target.planInfo?.remark ?? '');
    const [nextServiceDate, setNextServiceDate] = useState(
      dateFromValue(defaultNextServiceDate) ?? new Date(),
    );

    const [nextServiceMileage, setNextServiceMileage] = useState<
      number | undefined
    >(defaultNextServiceMileage);

    const isInsurance = target.type === 'insurance-expiry';

    const getValues = useCallback(() => {
      if (target.type === 'custom' && !customPlanName?.trim()) {
        void alert({ title: '请填写保养计划项目名称!' });
        return;
      }
      if (isBefore(nextServiceDate, new Date())) {
        void alert({
          title: isInsurance
            ? '保险到期日期不能早于今天'
            : '下次保养日期不能早于今天',
        });
        return undefined;
      }
      const name =
        target.type === 'custom' ? customPlanName.trim() : target.name;
      return {
        id: target.id,
        name,
        type: target.type,
        remark: remark.trim() || undefined,
        nextServiceDate,
        nextServiceMileage: isInsurance ? undefined : nextServiceMileage,
      };
    }, [
      alert,
      customPlanName,
      isInsurance,
      nextServiceDate,
      nextServiceMileage,
      remark,
      target.id,
      target.name,
      target.type,
    ]);

    useImperativeHandle(ref, () => ({
      getValues,
    }));

    const onNextServiceDateChange = useCallback(
      (_e: DateTimePickerEvent, date?: Date) => {
        const value = date ?? new Date();
        setNextServiceDate(value);
        if (
          target.planInfo?.nextServiceDate != null &&
          format(
            dateFromValue(target.planInfo.nextServiceDate),
            'yyyy-MM-dd',
          ) !== format(value, 'yyyy-MM-dd')
        ) {
          // reset next service mileage if date matches existing value
          setNextServiceMileage(undefined);
        }
      },
      [target.planInfo?.nextServiceDate],
    );

    return (
      <StackLayout
        gap={rpx(12)}
        css={`
          padding: ${rpx(15)}px ${rpx(20)}px;
        `}
      >
        {target.type === 'custom' ? (
          <Typography.Title level={1} align="center">
            设置自定义保养提醒计划
          </Typography.Title>
        ) : isInsurance ? (
          <Typography.Title level={1} align="center">
            设置保险到期提醒
          </Typography.Title>
        ) : (
          <Typography.Title level={1} align="center">
            设置
            <Text
              style={{
                opacity: 0.6,
              }}
            >{`"${target.name}"`}</Text>
            的下次保养提醒信息
          </Typography.Title>
        )}

        {target.type === 'custom' && (
          <View>
            <Typography.Label medium mb={rpx(5)}>
              保养计划项目名称:{' '}
            </Typography.Label>
            <Input
              placeholder="项目名称"
              value={customPlanName}
              onChangeText={setCustomPlanName}
              containerStyle={focus => ({
                borderColor: focus ? StatusColors.Info : '#f5f5f5',
              })}
              css={`
                background-color: #f5f5f5;
                border-radius: ${rpx(8)}px;
                font-weight: 500;
                font-size: ${rpx(15)}px;
                min-height: ${rpx(44)}px;
                padding-left: ${rpx(10)}px;
                padding-right: ${rpx(10)}px;
                padding-top: ${rpx(2)}px;
                padding-bottom: ${rpx(2)}px;
              `}
            />
          </View>
        )}

        <StackLayout gap={rpx(5)}>
          <Typography.Label medium>
            {isInsurance ? '保险到期日期: ' : '下次保养日期: '}
          </Typography.Label>
          <DateTimePicker
            mode="date"
            display="spinner"
            value={nextServiceDate}
            onChange={onNextServiceDateChange}
            style={{
              backgroundColor: '#f5f5f5',
              borderRadius: rpx(8),
              overflow: 'hidden',
            }}
          />
        </StackLayout>

        <StackLayout gap={rpx(5)}>
          {!isInsurance && (
            <>
              <Typography.Label medium>下次保养里程 (公里): </Typography.Label>
              <NumberInput
                value={nextServiceMileage}
                onChangeValue={setNextServiceMileage}
                placeholder="可选, 默认自动估计"
                placeholderTextColor={'#aaa'}
                returnKeyLabel="确认"
                returnKeyType="done"
                keyboardType="number-pad"
                inputMode="numeric"
                autoCapitalize="none"
                autoCorrect={false}
                focusedStyle={{
                  borderColor: StatusColors.Info,
                }}
                style={{
                  borderWidth: rpx(2),
                  borderColor: '#f5f5f5',
                  borderStyle: 'solid',
                }}
                css={`
                  background-color: #f5f5f5;
                  border-radius: ${rpx(8)}px;
                  font-weight: 500;
                  font-size: ${rpx(15)}px;
                  height: ${rpx(44)}px;
                  padding-left: ${rpx(10)}px;
                  padding-right: ${rpx(10)}px;
                  padding-top: ${rpx(2)}px;
                  padding-bottom: ${rpx(2)}px;
                `}
              />
            </>
          )}

          <View>
            <Typography.Label medium mb={rpx(5)}>
              备注:{' '}
            </Typography.Label>
            <Input
              value={remark}
              multiline
              numberOfLines={0}
              onChangeText={setRemark}
              placeholder="相关备注信息"
              withVoiceInput={isVoiceInputEnabled}
              containerStyle={focus => ({
                borderColor: focus ? StatusColors.Info : '#f5f5f5',
              })}
              css={`
                background-color: #f5f5f5;
                border-radius: ${rpx(8)}px;
                font-weight: 500;
                font-size: ${rpx(15)}px;
                height: ${rpx(100)}px;
                padding-left: ${rpx(10)}px;
                padding-right: ${rpx(10)}px;
                padding-top: ${rpx(5)}px;
                padding-bottom: ${rpx(5)}px;
              `}
            />
          </View>
        </StackLayout>
      </StackLayout>
    );
  }),
);
